import { connect } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, CircularProgress, Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import Page from "../../components/Page/Page";
import { Profile } from "../User/userDTO";
import { StreamTargetKind, StreamTargetList } from "../StreamTarget/streamTargetDTO";
import { StreamTargetIntegration } from "../StreamTargetIntegration/streamTargetIntegrationDTO";
import { fetchStreamTargetIntegrations } from "../StreamTargetIntegration/streamTargetIntegrationActions";
import { listTargets } from "../StreamTargetLive4tvApi/live4tvApiActions";
import {
    isInstanceOfLive4tvApiStreamTarget,
    Live4tvApiStreamTarget,
} from "../StreamTargetLive4tvApi/live4tvApiDTO";
import {
    live4tvLive4tvApiService,
    ScheduledLiveResponse,
} from "../StreamTargetLive4tvApi/live4tvLive4tvApiService";
import ScheduledLiveEditor from "./ScheduledLiveEditor";

type Properties = {
    profile: Profile;
    streamTargets: StreamTargetList;
    streamTargetIntegrations: Array<StreamTargetIntegration>;
    fetchStreamTargetIntegrations: Function;
    listTargets: Function;
};

function ScheduleLivePage(props: Properties) {
    const {
        profile,
        streamTargets,
        streamTargetIntegrations,
        fetchStreamTargetIntegrations,
        listTargets,
    } = props;
    const navigate = useNavigate();
    const { scheduledLiveUuid } = useParams<{ scheduledLiveUuid: string }>();
    const { t } = useTranslation();

    const [scheduledLive, setScheduledLive] = useState<ScheduledLiveResponse | null>(null);
    const [isLoading, setIsLoading] = useState(!!scheduledLiveUuid);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [warning, setWarning] = useState<string | null>(null);

    useEffect(() => {
        if (profile == null || profile.token === undefined) {
            navigate("/");
            return;
        }

        fetchStreamTargetIntegrations(profile.token);
        listTargets({ profile });
    }, [fetchStreamTargetIntegrations, listTargets, navigate, profile]);

    useEffect(() => {
        if (!profile?.token || !scheduledLiveUuid) {
            setScheduledLive(null);
            setIsLoading(false);
            return;
        }

        const loadScheduledLive = async () => {
            setIsLoading(true);
            try {
                const response = await live4tvLive4tvApiService.getScheduledLive(profile.token, scheduledLiveUuid);
                setScheduledLive(response);
                setError(null);
            } catch (loadError: any) {
                setError(loadError?.message || t("Failed to load the scheduled live."));
            } finally {
                setIsLoading(false);
            }
        };

        loadScheduledLive();
    }, [profile?.token, scheduledLiveUuid, t]);

    const availableScheduledTargets = useMemo(() => {
        const supportedTypes = new Set(
            streamTargetIntegrations
                .filter((integration) => integration.supportsScheduling)
                .map((integration) => integration.code),
        );

        return (Object.values(streamTargets) as Array<any>).filter((target) => {
            return isInstanceOfLive4tvApiStreamTarget(target)
                && target.targetKind !== StreamTargetKind.SCHEDULED_LIVE
                && supportedTypes.has(target.type);
        }).sort((left: Live4tvApiStreamTarget, right: Live4tvApiStreamTarget) => {
            return left.title.localeCompare(right.title);
        }) as Array<Live4tvApiStreamTarget>;
    }, [streamTargetIntegrations, streamTargets]);

    return (
        <Page title={t("Schedule Live")}>
            <Grid container direction="column" spacing={4}>
                <Grid item>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                        <Box>
                            <Typography variant="h5" gutterBottom>
                                {scheduledLiveUuid ? t("Edit Scheduled Live") : t("Schedule Live")}
                            </Typography>
                            <Typography color="text.secondary">
                                {t("Create or edit a scheduled live target independently from your events.")}
                            </Typography>
                        </Box>
                        <Button variant="outlined" onClick={() => navigate("/scheduled-lives")}>
                            {t("Back to scheduled lives")}
                        </Button>
                    </Box>
                </Grid>

                {error ? (
                    <Grid item>
                        <Alert severity="error">{error}</Alert>
                    </Grid>
                ) : null}

                {success ? (
                    <Grid item>
                        <Alert severity="success">{success}</Alert>
                    </Grid>
                ) : null}

                {warning ? (
                    <Grid item>
                        <Alert severity="warning">{warning}</Alert>
                    </Grid>
                ) : null}

                <Grid item>
                    {isLoading ? (
                        <Box display="flex" justifyContent="center" py={6}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <ScheduledLiveEditor
                            profile={profile}
                            scheduledLive={scheduledLive}
                            availableScheduledTargets={availableScheduledTargets}
                            onSaved={async (savedScheduledLive: ScheduledLiveResponse) => {
                                setError(null);
                                setWarning(null);
                                listTargets({ profile });
                                const warningMessage = savedScheduledLive.failures?.map((failure) => failure.message).join(" ") || null;
                                if (!scheduledLiveUuid) {
                                    navigate("/scheduled-lives", {
                                        replace: true,
                                        state: {
                                            success: t("Scheduled live created successfully."),
                                            warning: warningMessage,
                                        },
                                    });
                                    return;
                                }

                                setScheduledLive(savedScheduledLive);
                                setSuccess(t("Scheduled live saved successfully."));
                                setWarning(warningMessage);
                            }}
                            onCancel={() => navigate("/scheduled-lives")}
                        />
                    )}
                </Grid>
            </Grid>
        </Page>
    );
}

const mapStateToProps = function (state: any) {
    return {
        profile: state.user.profile,
        streamTargets: state.streamTargets,
        streamTargetIntegrations: state.streamTargetIntegrations,
    };
};

const mapDispatchToProps = function (dispatch: any) {
    return {
        fetchStreamTargetIntegrations: (token: string) => dispatch(fetchStreamTargetIntegrations(token)),
        listTargets: (request: any) => dispatch(listTargets(request)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(ScheduleLivePage);
